import { ReactElement, useEffect, useRef } from "react";
import { MapContainer, WMSTileLayer, ZoomControl } from "react-leaflet";
import { useMediaQuery } from "react-responsive";
import { AccordionView } from "../../common/accordion/accordion.view";
import { IAccordionData } from "../../common/interfaces/accordionData";
import { getOwnerId } from "../../common/utilities/roleHelpers";
import EstateAddresses from "./EstateAddresses";
import EstateBuildings from "./EstateBuildings";
import EstateCoreInformation from "./EstateCoreInformation";
import EstateDocuments from "./EstateDocuments";
import EstateGrunnbok from "./EstateGrunnbok";
import { BootstrapSpinner } from "../../common/spinners/bootstrapSpinner";
import { useMunicipality } from "../../Contexts/MunicipalityKey";
import { useEstateConfig, useSweepingConfig } from "../../common/config/configHooks";
import EstateSweeping from "./EstateSweeping";
import { useEstateDataQuery } from "../../Requests/estateApi";
import { matrikkelString } from "../../common/utilities/matrikkel";
import { AddressNames, IEstate } from "../../Types/EstateTypes";
import { useAppSelector } from "../../Hooks";
import 'leaflet/dist/leaflet.css';
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";

const EstatePage = () => {
  const municipality = useMunicipality();
  const estateConfig = useEstateConfig();
  const sweepingConfig = useSweepingConfig();
  
  const showForMunicipalityNumbers = municipality.number.toString();
  const mapRef = useRef<any>();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });


  const activeRole = useAppSelector(state => state.role.activeRole);
  const ownerId = getOwnerId(activeRole);
  const {data:estates, isFetching: loadingEstates } = useEstateDataQuery(ownerId)

  useEffect(() => {
  }, [estates, ownerId, loadingEstates]);

  const createSubAccordionData = (data: IEstate) => {
    let subAccordions: {
      title: string;
      content: ReactElement;
      onClick?: Function;
    }[] = [];

    if (
      data.addresses &&
      data.addresses.addressNames != null &&
      (data.addresses.addressNames.length > 1 ||
        data.addresses.addressNames.filter(
          (adressNames: AddressNames) => adressNames.unitNumbers.length > 1
        ).length > 0)
    ) {
      subAccordions.push({
        title: "Adresser",
        content: <EstateAddresses addressNames={data.addresses.addressNames} />,
      });
    }

    if (data.buildings && data.buildings.length > 0) {
      subAccordions.push({
        title: "Bygninger",
        content: <EstateBuildings buildings={data.buildings} />,
      });
    }

    if (
      showForMunicipalityNumbers.includes(
        data.ownerRelation.matrikkelenhetIdent.kommunenr
      ) &&
      municipality.name !== "vennesla"
    ) {
      subAccordions.push({
        title: "Dokumenter (Doctorg)",
        content: <EstateDocuments />,
      });
    }

    subAccordions.push({
      title: "Grunnboksinformasjon",
      content: (
        <EstateGrunnbok
          matrikkelenhetIdent={data.ownerRelation.matrikkelenhetIdent}
        />
      ),
    });

    if (sweepingConfig && sweepingConfig.enabled && !sweepingConfig.hideService){
      subAccordions.push({
        title: "Feiing og boligtilsyn",
        content: (<EstateSweeping config={sweepingConfig} />),
      });
    }

    return subAccordions;
  };

  const createAccordionData = (): IAccordionData[] => {
    let accordionData: { title: string; content: JSX.Element }[] = [];

    if (estates && estates.length > 0) {
      estates.map((data: IEstate) => {
        const matrikkelenhetIdent = data.ownerRelation.matrikkelenhetIdent;
        return accordionData.push({
          title: data.address
            ? data.address.text
            : matrikkelString(matrikkelenhetIdent),
          content: (
            <>
              <EstateCoreInformation
                estate={data}
                address={
                  data.address && data.address.text.length > 0
                    ? data.address.text
                    : undefined
                }
              />
              <AccordionView
                showNarrow={true}
                hideBorder={true}
                content={createSubAccordionData(data)}
              />
              {data.addresses &&
              data.addresses.addressList &&
              data.addresses.addressList.length === 1 ? (
                <MapContainer
                  style={{
                    height: "500px",
                  }}
                  center={[
                    data.address.coordinates.lat,
                    data.address.coordinates.lon,
                  ]}
                  dragging={!!!isTabletOrMobile}
                  zoom={19}
                  scrollWheelZoom={false}
                  zoomControl={false}
                >
                  <ZoomControl position={"topleft"} />
                  <WMSTileLayer
                    layers="webatlas-gray-vektor"
                    url={`https://www.webatlas.no/maptiles/wms?apitoken=${"ec0b3777-596e-4dce-b309-3e00d4483b77"}`}
                    maxZoom={25}
                    minZoom={14}
                    format="image/png"
                    attribution="&copy; 2023 Norkart AS/Geovekst og kommunene/OpenStreetMap/NASA, Meti, Norkart AS, Norge Digitalt"
                  />
                  <WMSTileLayer
                    layers="Eiendomskart_Bygningsnr"
                    url={`https://waapi.webatlas.no/wms-matrikkelkart/?apitoken=${"ec0b3777-596e-4dce-b309-3e00d4483b77"}`}
                    maxZoom={25}
                    minZoom={14}
                    format="image/png"
                    transparent={true}
                    attribution=""
                  />
                </MapContainer>
              ) : (
                ""
              )}
            </>
          ),
        });
      });
    }

    return accordionData;
  };

  if (loadingEstates)
    return <BootstrapSpinner title="Henter eiendommene dine" />;
  else
    return (
      <>
        <div className="container my-properties">
          <h1>Eiendommer</h1>
          {estateConfig?.hideService ? (
            "Tjenesten er dessverre ikke tilgjengelig."
          ) : (
            <>
              {estateConfig?.alertText &&
                estateConfig?.alertText !== "" && (
                  <div className="infoBoxLanding">
                    <p>{estateConfig?.alertText}</p>
                  </div>
                )}
              <p>{estateConfig?.ingressText}</p>
              <AccordionView
                content={createAccordionData()}
                mapref={mapRef}
                hideBorder={true}
              />
            </>
          )}
        </div>
      </>
    );
};

export default EstatePage;
